import clsx from "clsx"
import {
  createContext,
  type PropsWithChildren,
  type ReactNode,
  useContext,
} from "react"

import { BackgroundIcon, type BackgroundIconProps } from "../BackgroundIcon"
import { Button } from "../Button"
import type { IconName } from "../Icon"
import { Text } from "../Text"
import styles from "./Banner.module.css"

type Color = "error" | "warn" | "info"
type ContentColor = "black" | "white"

export interface BannerProps {
  children?: ReactNode
  color?: Color
  iconName?: IconName
  onClose: () => void
}

export function Banner(props: BannerProps) {
  const { children, color = "info", iconName = "alert", onClose } = props

  return (
    <BannerContext.Provider value={{ color }}>
      <section
        className={clsx(styles.banner, styles[`containerColor-${color}`])}
        role="alert"
      >
        <div className={styles.content}>
          <BannerIcon name={iconName} />
          <div className={styles.bannerMessage}>{children}</div>
        </div>
        <div className={styles.closeButton}>
          <BannerButton aria-label="Close banner" onClick={onClose}>
            Close
          </BannerButton>
        </div>
      </section>
    </BannerContext.Provider>
  )
}

/**
 * @internal
 * Banner context used to propagate the color for all items in the Banner.
 */
type BannerContext = { color: Color }

const BannerContext = createContext<BannerContext>({
  color: "info",
})

function useBanner() {
  return useContext(BannerContext)
}

function getContentColor(color: Color): ContentColor {
  return color === "warn" ? "black" : "white"
}

const severityToColor: Record<Color, BackgroundIconProps["color"]> = {
  info: "purple",
  warn: "amber",
  error: "tomato",
}

interface BannerIconProps {
  name?: IconName
}

function BannerIcon(props: BannerIconProps) {
  const { name = "alert" } = props
  const { color } = useBanner()

  return (
    <BackgroundIcon
      name={name}
      size="extraSmall"
      variant="solid"
      color={severityToColor[color]}
    />
  )
}

export function BannerTitle(props: PropsWithChildren) {
  const { children } = props
  const { color } = useBanner()

  return (
    <Text variant="bold" color={getContentColor(color)} component="header">
      {children}
    </Text>
  )
}

export function BannerDescription(props: PropsWithChildren) {
  const { children } = props
  const { color } = useBanner()

  return (
    <Text variant="normal" color={getContentColor(color)}>
      {children}
    </Text>
  )
}

export interface BannerButtonProps {
  asChild?: boolean
  children?: ReactNode
  onClick?: () => void
}

export function BannerButton(props: BannerButtonProps) {
  const { asChild, children, onClick } = props
  const { color } = useBanner()

  return (
    <Button
      asChild={asChild}
      onClick={onClick}
      color={getContentColor(color)}
      invisible
      underline="hover"
    >
      {children}
    </Button>
  )
}
